/**
 * Consumer-side reading of the `epa-attainment` provider result (EPA Green
 * Book NAYRO county designations).
 *
 * ABSENCE IS NEVER ATTAINMENT:
 *  - no result, or a result without `coverage` (produced before the national
 *    table existed, when a one-row stub answered "not nonattainment" for every
 *    county but Los Angeles) → 'not-assessed';
 *  - a county absent from the national table → 'not-listed': no current
 *    nonattainment or maintenance designation listed as of the EPA export
 *    date, which is narrower than an attainment determination.
 *
 * Type-only import of the provider: callers do not pull in the provider or
 * its bundled table. Only the table's ~3 KB provenance header is imported, for
 * the export year (`PAST_UNTIL`).
 */
import type {
  EpaAttainmentData,
  EpaDesignationStatus,
  NaaqsDesignation,
} from '../../services/dataProviders/epaAttainment';
import epaNonattainmentTableMeta from '../../data/epaOzoneNonAttainment.meta.json';

export type EpaDesignationReading = EpaDesignationStatus | 'not-assessed';

/**
 * The county's listing. Any current nonattainment row wins, whatever the
 * stored headline says: the provider derives `designationStatus` by the same
 * rule (`designationStatusOf`) and the Stage 2 air rule scans the rows, so a
 * block whose headline disagrees with its rows (hand-edited, or written by a
 * future producer) still scores and reads as nonattainment. The correction
 * runs in the stricter direction only; a stored 'not-listed' with maintenance
 * rows is never promoted to 'maintenance' (Attainment 85).
 */
export function readEpaDesignation(epa: EpaAttainmentData | null | undefined): EpaDesignationReading {
  if (!epa?.coverage || !epa.designationStatus) return 'not-assessed';
  if ((epa.designations ?? []).some(d => d.status === 'nonattainment')) return 'nonattainment';
  return epa.designationStatus;
}

function exportDateOf(coverage: string): string {
  return /(\d{4}-\d{2}-\d{2})$/.exec(coverage)?.[1] ?? coverage;
}

/** EPA export date (ISO) behind a coverage-backed result, or null. */
export function epaDesignationAsOf(epa: EpaAttainmentData | null | undefined): string | null {
  return epa?.coverage ? exportDateOf(epa.coverage) : null;
}

const MENTION = String.raw`non-?attainment\b`;
/**
 * One word of the same clause: no clause punctuation ("PM2.5", "EPA’s",
 * "Metro/North" stay one word), and a parenthesised one ("(2024)", "(Severe)")
 * does not end the clause.
 */
const CLAUSE_WORD = String.raw`\(?[A-Za-z0-9'’/-]+(?:\.\d+)?\)?`;
/** Conjunctions that start a new clause and so end a negation's reach. */
const CLAUSE_BREAK = String.raw`(?:and|but|or|yet|although|though|while|whereas|however)\b`;
const clauseWords = (count: string): string => String.raw`(?:\s+(?!${CLAUSE_BREAK}|${MENTION})${CLAUSE_WORD})${count}`;
/**
 * Negations that do not deny the designation: "not only a … area", "has not
 * been revoked", "not outside the … area" (inside it).
 */
const AFFIRMING = String.raw`(?!\s+(?:yet\s+)?(?:been\s+)?(?:only|revoked|lifted|rescinded|withdrawn|redesignated|outside|beyond)\b)`;
/** A modal "would / could … not be" is hypothetical, not a denial. */
const NOT_AFTER_MODAL = String.raw`(?<!\b(?:would|could|might|will|may|should|must|can|shall)\s+)`;
const NT_NOT_MODAL = String.raw`(?<!\b(?:would|could|might|should|must|wo|ca|sha))`;

/**
 * Negated mentions, each removed to the end of its own clause, so one
 * negated mention cannot mask a current one elsewhere in the text.
 */
const NEGATION_SPANS: readonly RegExp[] = [
  // "not a nonattainment area", "isn't currently designated as a serious
  // ozone nonattainment area", "no longer in non-attainment", "not part of
  // the Metro/North … area" — but not "not only a nonattainment area", "has
  // not revoked the nonattainment …", "not outside the … area" or "would not
  // be in compliance in a nonattainment area".
  new RegExp(String.raw`(?:${NOT_AFTER_MODAL}\b(?:not|never|cannot)|${NT_NOT_MODAL}n['’]t|\bno\s+longer)${AFFIRMING}${clauseWords('{0,6}?')}\s+${MENTION}${clauseWords('*')}`, 'gi'),
  // "no current nonattainment designation", "no evidence of nonattainment".
  new RegExp(String.raw`\bno${clauseWords('{0,2}?')}\s+${MENTION}${clauseWords('*')}`, 'gi'),
];
/** Mentions that are not claims either and state no designation. */
const PAIRING_SPANS: readonly RegExp[] = [
  // Either/or: "attainment/nonattainment status", "nonattainment or attainment".
  /\battainment\s*(?:\/|\bor\b|\band\b)\s*non-?attainment\b|\bnon-?attainment\s*(?:\/|\bor\b)\s*attainment\b/gi,
  // Comparisons: "cheaper than in nonattainment areas", "unlike nonattainment areas".
  /\b(?:compared\s+(?:to|with)|than|unlike)\s+(?:in\s+)?non-?attainment\b/gi,
];
/**
 * A cue that a mention may be ANOTHER area's or generic: "the nearest
 * nonattainment area … 60 miles east", "neighbouring Harris County is in …",
 * "borders the … area", "offsets required in nonattainment areas".
 */
const OTHER_AREA_CUE = /\b(?:nearest|closest|nearby|neighbou?r(?:s|ing)?|adjacent|adjoining|abut(?:s|ting)?|borders?|bordering|bordered|surrounding|upwind|downwind|outside|beyond|elsewhere|another|other\s+(?:areas?|count(?:y|ies)|parish(?:es)?|regions?|states?)|\d[\d,.]*\s*(?:miles?|mi|km|kilomet(?:er|re)s?))\b|\bnon-?attainment\s+areas\b/i;
const NONATTAINMENT_MENTION = new RegExp(String.raw`\b${MENTION}`, 'i');
/** Nonattainment stated as failed attainment. */
export const NOT_IN_ATTAINMENT = /\bnot\s+(?:currently\s+|yet\s+)?(?:in\s+attainment|an\s+attainment\s+area)\b/i;

const stripSpans = (text: string, spans: readonly RegExp[]): string => spans.reduce((rest, span) => rest.replace(span, ' '), text);
/** Sentence and clause boundaries: a cue in one says nothing about a mention in another. */
const SEGMENT_BREAK = /(?<=[.;!?])\s+|\n+/;
const statesNonattainmentIn = (segment: string): boolean =>
  NONATTAINMENT_MENTION.test(segment) || NOT_IN_ATTAINMENT.test(segment);

/** "revoked" that is not itself negated ("has not been revoked", "hasn't been revoked"). */
const NOT_NEGATED = String.raw`(?<!(?:\bnot|\bnever|n['’]t)(?:\s+(?:yet|been|ever))*\s+)`;
/** Clause words that make a historical adverb's clause present or present-perfect ("historically has been", "formerly … is"). */
const PRESENT_VERB = String.raw`(?:is|are|has|have)\b`;
/** An area classification: "Serious", "Severe-15", "(Moderate)". */
const CLASS_WORD = String.raw`\(?(?:marginal|moderate|serious|severe|extreme)(?:[- ]\d+)?\b\)?`;
const NAMES_CLASS = /\b(?:marginal|moderate|serious|severe|extreme)\b/i;
/**
 * A word that can only describe a standard: an article, a year, an averaging
 * period, a pollutant, "standard", "NAAQS", "revoked", "it". A qualifier built
 * from these cannot bring in another subject: "under the revoked 1997 ozone
 * standard", but not "under the Clean Air Act whose sanctions".
 */
const STANDARD_WORD = String.raw`(?:(?:the|a|an|its|this|it|revoked|former|prior|original|(?:19|20)\d{2}|(?:1|8|24|one|eight|twenty-four)[- ]hours?|annual|daily|ozone|O3|PM[- ]?2\.5|PM[- ]?10|fine|particulates?|matter|lead|Pb|SO2|sulph?ur|NO2|nitrogen|dioxide|CO|carbon|monoxide|standards?|standard['’]s|NAAQS)\b|\((?:19|20)\d{2}\))`;
const standardWords = (count: string): string => String.raw`(?:\s+${STANDARD_WORD})${count}`;
/** The designation itself, as the subject of a retirement verb. */
const DESIGNATION_NOUN = String.raw`(?:designations?|status|classifications?)\b`;
/** Auxiliaries between the designation and its retirement verb ("was", "has since been", "which was later"). */
const AUXILIARIES = String.raw`(?:\s+(?:which|that))?(?:\s+(?:is|are|was|were|has|have|had|been|being|since|subsequently|later|formally)\b)*`;
/**
 * Mentions whose own wording retires them, each removed to the end of its own
 * clause. Applied only where a negation was stripped (see
 * `nonattainmentClaimText`).
 *  - A revoked standard named before the mention, in words that can only
 *    describe that standard: "the revoked 1997 standard's nonattainment
 *    designation", "revoked 1-hour ozone nonattainment"; not "once the 1-hour
 *    standard was revoked the county became a severe nonattainment area".
 *  - A retirement verb whose subject is the designation itself: "the
 *    nonattainment designation was revoked in 2005", "the county's
 *    nonattainment designation under the revoked 1997 ozone standard is no
 *    longer in effect"; not "a nonattainment area whose sanctions were lifted
 *    in 2020" or "the designation under the Clean Air Act whose sanctions were
 *    lifted", where something else was lifted.
 *  - A standard marked revoked right after it: "nonattainment for 1-hour ozone
 *    (revoked)", "(revoked in 2005)"; not "(revoked 1997 standard excluded)".
 */
const RETIRED_SPANS: readonly RegExp[] = [
  new RegExp(String.raw`${NOT_NEGATED}\brevoked${standardWords('{0,6}?')}\s+${MENTION}${clauseWords('*')}`, 'gi'),
  new RegExp(String.raw`\b${MENTION}(?:\s+(?:areas?\b|${CLASS_WORD})){0,2}?\s+${DESIGNATION_NOUN}(?:\s+(?:under|for)${standardWords('{1,6}?')})?${AUXILIARIES}\s+(?:(?:revoked|rescinded|withdrawn|lifted)\b|no\s+longer\s+(?:applies|apply|in\s+effect|effective|current|valid)\b)${clauseWords('*')}`, 'gi'),
  new RegExp(String.raw`\b${MENTION}(?:\s+(?:(?:for|under|areas?)\b|${CLASS_WORD}|${STANDARD_WORD})){0,8}?\s+\(\s*(?:revoked|rescinded|withdrawn)(?:\s+(?:in\s+)?(?:19|20)\d{2})?\s*\)${clauseWords('*')}`, 'gi'),
];
/**
 * "formerly …": the designation ended. When the span names a class, the class
 * may be what ended ("formerly Marginal"), so it then needs a cessation of the
 * same standard (`retiredBy`).
 */
const FORMERLY = new RegExp(String.raw`(?<!\b(?:has|have)\s+)\bformerly(?:\s+(?!${CLAUSE_BREAK}|${MENTION}|${PRESENT_VERB})${CLAUSE_WORD}){0,6}?\s+${MENTION}${clauseWords('*')}`, 'gi');
/**
 * "historically …", "previously …" date a designation without ending it ("the
 * Houston area, historically a severe ozone nonattainment area, …", "EPA
 * previously designated the county … in 2019"), so they need a cessation of
 * the same standard (`retiredBy`). "Has historically been" (present perfect)
 * never matches.
 */
const PAST_ADVERB = new RegExp(String.raw`(?<!\b(?:has|have)\s+)\b(?:historically|previously)(?:\s+(?!${CLAUSE_BREAK}|${MENTION}|${PRESENT_VERB})${CLAUSE_WORD}){0,6}?\s+${MENTION}${clauseWords('*')}`, 'gi');
/**
 * A past-tense "was … nonattainment … until <year>", with only words
 * describing the standard between the mention and "until" and no attainment
 * deadline after the year ("until 2024 to attain"). Present tense ("is a
 * severe ozone nonattainment area with obligations until 2027") and a deadline
 * or obligation ("with an attainment date until 2027") never match. The year
 * decides (`withoutHistoricalMentions`):
 *  - after the shipped export's year, the claim is current ("… until 2031");
 *  - before it, with no class named, the designation ended ("the county was in
 *    nonattainment until 2019");
 *  - the export year itself, or a class named ("was a serious ozone
 *    nonattainment area until 2022, when EPA bumped it up to severe", where
 *    the class ended), needs a cessation of the same standard (`retiredBy`).
 * The export year, not the clock, keeps the reading reproducible for a build.
 */
const PAST_UNTIL = new RegExp(String.raw`\b(?:was|were|had\s+been|remained)${clauseWords('{0,6}?')}\s+${MENTION}(?:\s+(?:(?:for|under|areas?)\b|${CLASS_WORD}|${STANDARD_WORD})){0,8}?\s+until\s+((?:19|20)\d{2})\b(?!\s+(?:(?:in\s+which\s+)?to|by)\s+(?:attain|comply|meet|achieve)\b)${clauseWords('*')}`, 'gi');
/** Year of the shipped Green Book export; 0 (every "until" reads as current) if the header has no date. */
const EXPORT_YEAR = Number(/^(\d{4})/.exec((epaNonattainmentTableMeta as { exportDate?: string }).exportDate ?? '')?.[1] ?? 0);
/**
 * A dated past-tense designation ("was designated nonattainment for the 1997
 * ozone standard in 2004"). On its own it may still be current, so it is
 * removed only beside a cessation the text states for the same standard.
 */
const PAST_DESIGNATION = new RegExp(String.raw`\b(?:was|were)\s+(?:(?:first|initially)\s+)?(?:designated|classified|listed)${clauseWords('{0,4}?')}\s+${MENTION}${clauseWords('*')}`, 'gi');
const DATED = /\b(?:in|on)\s+(?:19|20)\d{2}\b/i;
/** When something happened ("in 2004", "until 2012"), which is not the year of a standard. */
const EVENT_DATE = /\b(?:in|on|until|since)\s+(?:19|20)\d{2}\b/gi;
/** A negation that retires a designation, as opposed to one that denies it outright. */
const CESSATION = /\bno\s+longer\b|\bnot\s+currently\b|\bno\s+current\b/i;
/** Present-tense wording: a clause that says the designation still holds keeps its mention. */
const CURRENT_ANCHOR = /\b(?:remains?|still|currently|presently|now|continues?\s+to|reclassified|bumped\s+up)\b/i;

const POLLUTANT_FAMILIES: ReadonlyArray<readonly [string, RegExp]> = [
  ['ozone', /\bozone\b|\bO3\b/i],
  ['pm2.5', /\bPM[- ]?2\.5\b|\bfine\s+particulate/i],
  ['pm10', /\bPM[- ]?10\b/i],
  ['so2', /\bSO2\b|\bsulph?ur\s+dioxide\b/i],
  ['no2', /\bNO2\b|\bnitrogen\s+dioxide\b/i],
  ['co', /\bCO\b|\bcarbon\s+monoxide\b/],
  ['lead', /\blead\b|\bPb\b/i],
];
/** Averaging periods, which tell standards of one pollutant apart (1-hour vs 8-hour ozone, 24-hour vs annual PM). */
const AVERAGING_PERIODS: ReadonlyArray<readonly [string, RegExp]> = [
  ['1-hour', /\b(?:1|one)[- ]hours?\b/i],
  ['8-hour', /\b(?:8|eight)[- ]hours?\b/i],
  ['24-hour', /\b(?:24|twenty[- ]four)[- ]hours?\b|\bdaily\b/i],
  ['annual', /\bannual\b/i],
];
const YEAR = /\b(?:19|20)\d{2}\b/g;
const REVOKED = new RegExp(String.raw`${NOT_NEGATED}\brevoked\b`, 'i');

interface NamedStandards { pollutants: Set<string>; years: Set<string>; periods: Set<string>; revoked: boolean; classified: boolean }
function namedStandards(text: string): NamedStandards {
  const pollutants = new Set(POLLUTANT_FAMILIES.filter(([, re]) => re.test(text)).map(([key]) => key));
  const periods = new Set(AVERAGING_PERIODS.filter(([, re]) => re.test(text)).map(([key]) => key));
  // "the 1-hour standard", "the 8-hour standard" name ozone when no pollutant is named ("8-hour CO" is CO).
  if (pollutants.size === 0 && (periods.has('1-hour') || periods.has('8-hour'))) pollutants.add('ozone');
  return {
    pollutants, periods, years: new Set(text.match(YEAR) ?? []), revoked: REVOKED.test(text), classified: NAMES_CLASS.test(text),
  };
}

/** A cessation about everything but the mention ("not currently in nonattainment for any other pollutant"). */
const OTHER_THAN = /\b(?:other|another|else|besides|except)\b/i;
/** A cessation naming a standard: without a year, pollutant or period it cannot be matched, unless it is "any standard". */
const NAMES_STANDARD = /\b(?:standards?|NAAQS)\b/i;
const ANY_STANDARD = /\b(?:any|all|every)\s+(?:\w+\s+)?(?:standards?|NAAQS)\b/i;

/**
 * Whether the cessations the text states retire a past mention. A cessation
 * naming a class ("no longer a serious nonattainment area") retires nothing:
 * it may be a reclassification. Nor does one about everything else ("… for
 * any other pollutant"), or one naming a standard it does not identify ("no
 * longer nonattainment under the new standard"). One naming no standard ("it
 * is no longer nonattainment", "… under any standard") retires any. Otherwise
 * every detail it names must be the mention's own:
 *  - standard years: the mention names one of them (the dates of events, "in
 *    2004" or "until 2012", are not standard years);
 *  - pollutants and averaging periods: the mention names one of each; when
 *    the standard years matched, a mention that names none is compatible
 *    ("the 1997 standard" beside "the 1997 8-hour ozone standard");
 *  - "revoked" with neither a year nor a period ("under the revoked
 *    standard"): the mention is itself marked revoked.
 * So "no longer nonattainment under the revoked 1-hour standard" leaves a
 * 2008 or 2015 ozone designation standing, and "no longer nonattainment for
 * the 24-hour PM2.5 standard" leaves a 2012 PM2.5 designation standing.
 */
function retiredBy(mention: string, cessations: readonly string[]): boolean {
  if (cessations.length === 0) return false;
  const stated = cessations.join(' ');
  const retired = namedStandards(stated);
  if (retired.classified || OTHER_THAN.test(stated)) return false;
  if (retired.years.size === 0 && retired.pollutants.size === 0 && retired.periods.size === 0 && !retired.revoked) {
    return !NAMES_STANDARD.test(stated) || ANY_STANDARD.test(stated);
  }
  const named = namedStandards(mention.replace(EVENT_DATE, ' '));
  const yearsMatch = [...retired.years].some(year => named.years.has(year));
  if (retired.years.size > 0 && !yearsMatch) return false;
  const agrees = (by: Set<string>, of: Set<string>): boolean =>
    by.size === 0 || [...by].some(key => of.has(key)) || (yearsMatch && of.size === 0);
  const revokedOnly = retired.revoked && retired.years.size === 0 && retired.periods.size === 0;
  return agrees(retired.pollutants, named.pollutants)
    && agrees(retired.periods, named.periods)
    && (!revokedOnly || named.revoked);
}

/** One negation-stripped segment without its historical or retired mentions. */
function withoutHistoricalMentions(segment: string, cessations: readonly string[]): string {
  if (CURRENT_ANCHOR.test(segment)) return segment;
  const retired = (mention: string): boolean => retiredBy(mention, cessations);
  return stripSpans(segment, RETIRED_SPANS)
    .replace(FORMERLY, span => (!NAMES_CLASS.test(span) || retired(span) ? ' ' : span))
    .replace(PAST_UNTIL, (span, year: string) => {
      if (Number(year) > EXPORT_YEAR) return span;
      return (Number(year) < EXPORT_YEAR && !NAMES_CLASS.test(span)) || retired(span) ? ' ' : span;
    })
    .replace(PAST_ADVERB, span => (retired(span) ? ' ' : span))
    .replace(PAST_DESIGNATION, span => (DATED.test(span) && retired(span) ? ' ' : span));
}

/**
 * The text left once every non-claim mention is removed, when it still states
 * nonattainment ("no longer nonattainment for the 1997 standard, but serious
 * nonattainment for 2015" keeps the 2015 clause); null when it does not.
 *
 * Per mention, against the reader it replaced (which skipped any text that
 * negated a designation and otherwise counted every mention):
 *  - Negations, either/or wording and comparisons are always masked.
 *  - Without a negation every other mention counts, as before, historical
 *    wording included ("formerly a nonattainment area for PM10 but
 *    redesignated" still reads as a claim).
 *  - Where a negation was stripped, a remaining mention is dropped when its
 *    sentence or clause carries an other-area cue, since it may be another
 *    area's ("not in the Houston area; the nearest nonattainment area is 60
 *    miles east" → null). A cue in a different sentence says nothing about the
 *    mention ("The county is a serious ozone nonattainment area; it is not a
 *    PM2.5 nonattainment area. The site is 5 miles from the county seat."
 *    keeps the ozone claim).
 *  - Where a negation was stripped, a historical or retired mention is dropped
 *    too, unless its clause says the designation still holds (remains,
 *    currently, now, reclassified). Wording that says the designation ended
 *    drops it on its own (`RETIRED_SPANS`, `FORMERLY`, `PAST_UNTIL`): a
 *    designation that was itself revoked, lifted or withdrawn, a revoked
 *    standard named with it, "formerly", and "was … until <year>" for a year
 *    before the export's. Wording that only dates it needs a cessation ("no
 *    longer", "not currently", "no current") of the same standard
 *    (`retiredBy`): a dated past-tense designation, "historically",
 *    "previously", and a "formerly" or "until" that names a class, which may
 *    be what ended ("was designated nonattainment for the 1997 standard in
 *    2004; it is no longer nonattainment" → null; beside "no longer
 *    nonattainment for PM2.5" or "… under the revoked 1-hour standard" a 2015
 *    ozone designation stays). A deadline or obligation "until 2027", or
 *    something else that was lifted ("whose sanctions were lifted"), never
 *    retires a designation.
 *  - So the only additions over the old reader are own-site claims in negated
 *    text; its only removals are negated, either/or, comparison, other-area
 *    and historical mentions.
 */
export function nonattainmentClaimText(text: string | null | undefined): string | null {
  if (!text) return null;
  const negations: string[] = [];
  const withoutNegations = NEGATION_SPANS.reduce(
    (rest, span) => rest.replace(span, negation => { negations.push(negation); return ' '; }),
    text,
  );
  const cessations = negations.filter(negation => CESSATION.test(negation));
  const kept = negations.length === 0
    ? text
    : withoutNegations
      .split(SEGMENT_BREAK)
      .map(segment => withoutHistoricalMentions(segment, cessations))
      .filter(segment => !(statesNonattainmentIn(segment) && OTHER_AREA_CUE.test(segment)))
      .join(' ');
  const remaining = stripSpans(kept, PAIRING_SPANS);
  return statesNonattainmentIn(remaining) ? remaining : null;
}

/**
 * The sentences and clauses of the claim text that still state nonattainment
 * (see `nonattainmentClaimText`); empty when the text makes no claim.
 */
export function nonattainmentClaimSegments(text: string | null | undefined): string[] {
  const claim = nonattainmentClaimText(text);
  return claim ? claim.split(SEGMENT_BREAK).filter(statesNonattainmentIn) : [];
}

/** True when research text positively states nonattainment, not when it negates it. */
export function researchTextClaimsNonattainment(text: string | null | undefined): boolean {
  return nonattainmentClaimText(text) !== null;
}

/**
 * How a coverage-backed EPA listing disagrees with site-research text:
 * 'research-overstates' (text claims nonattainment; EPA lists none, e.g. a
 * revoked standard) or 'research-misses' (EPA lists nonattainment; the text
 * does not). Null when they agree, the text is empty, or EPA was not assessed.
 */
export function epaResearchConflict(
  epa: EpaAttainmentData | null | undefined,
  researchText: string | null | undefined,
): 'research-overstates' | 'research-misses' | null {
  const reading = readEpaDesignation(epa);
  if (reading === 'not-assessed' || !researchText?.trim()) return null;
  const claims = researchTextClaimsNonattainment(researchText);
  if (claims && (reading === 'not-listed' || reading === 'maintenance')) return 'research-overstates';
  if (!claims && reading === 'nonattainment') return 'research-misses';
  return null;
}

/** EPA-7 — a part-county row EPA's boundary resolved at the site point. */
function resolvedContainment(d: NaaqsDesignation): 'inside' | 'outside' | null {
  const status = d.extent === 'part' ? d.siteContainment?.status : undefined;
  return status === 'inside' || status === 'outside' ? status : null;
}

function designationLabel(d: NaaqsDesignation): string {
  const containment = resolvedContainment(d);
  const part = d.extent !== 'part'
    ? ''
    : containment
      ? `; part of the county; site point ${containment} this area per EPA boundary GIS`
      : '; part of the county';
  return `${d.naaqs}${d.classification ? ` ${d.classification}` : ''} (${d.area}${part})`;
}

const OZONE_RANK = ['marginal', 'moderate', 'serious', 'severe', 'extreme'] as const;
const ozoneRank = (className: string | undefined): number => {
  const match = className ? /\b(marginal|moderate|serious|severe|extreme)\b/i.exec(className) : null;
  return match ? OZONE_RANK.indexOf(match[1]!.toLowerCase() as typeof OZONE_RANK[number]) : -1;
};

/**
 * EPA-7 — the site-point line for the most stringent 8-hour ozone class, when
 * EPA's boundary excludes the point from the county's most stringent area;
 * null otherwise (no resolved exclusion, or no ozone listing).
 */
function siteOzoneClassLine(epa: EpaAttainmentData): string | null {
  const ozone = (epa.designations ?? []).filter(d => d.status === 'nonattainment' && d.pollutant === 'ozone' && /^8-Hour Ozone\b/.test(d.naaqs));
  const excluded = ozone.filter(d => resolvedContainment(d) === 'outside');
  if (!epa.classification || excluded.length === 0) return null;
  const atPoint = ozone.filter(d => !excluded.includes(d));
  const top = atPoint.reduce<NaaqsDesignation | null>((best, d) => (ozoneRank(d.classification) > ozoneRank(best?.classification) ? d : best), null);
  if (!top) return 'No current 8-hour ozone nonattainment area contains the site point per EPA boundary GIS.';
  if (ozoneRank(top.classification) === ozoneRank(epa.classification)) return null;
  const unresolved = top.extent === 'part' && resolvedContainment(top) !== 'inside';
  return `Most stringent 8-hour ozone classification that can apply at the site point: ${top.classification} (EPA boundary GIS places the point outside the ${epa.classification} area${unresolved ? '; the remaining area covers only part of the county' : ''}).`;
}

/**
 * One dated clause (no trailing period) for SED text and cross-check
 * messages. An unlisted or unassessed county is never rendered as attainment.
 */
export function describeEpaDesignation(epa: EpaAttainmentData): string {
  const where = `${epa.county}, ${epa.state} (FIPS ${epa.fips})`;
  const reading = readEpaDesignation(epa);
  if (reading === 'not-assessed') {
    return `EPA NAAQS designation not assessed for ${where}: the lookup result predates the national Green Book table`;
  }
  const source = `EPA Green Book (NAYRO export ${exportDateOf(epa.coverage!)})`;
  const designations = epa.designations ?? [];
  if (reading === 'not-listed') {
    return `${source} lists no current nonattainment or maintenance area for ${where}: a screening result, not an attainment determination (40 CFR Part 81 is authoritative)`;
  }
  if (reading === 'maintenance') {
    const standards = [...new Set(designations.map(d => d.naaqs))].join(', ');
    return `${source} lists ${where} only in maintenance areas (redesignated to attainment) for ${standards}; no current nonattainment designation`;
  }
  const nonattainment = designations.filter(d => d.status === 'nonattainment').map(designationLabel).join('; ');
  return `${source} lists ${where} in nonattainment: ${nonattainment}`;
}

/**
 * The EPA designation block `sedToText` prints inside the ENVIRONMENTAL
 * section (projected into the feasibility and environmental reports). An
 * SED without the block says so explicitly rather than implying attainment.
 */
export function formatEpaDesignationsForPrompt(epa: EpaAttainmentData | null | undefined): string {
  const heading = 'EPA NAAQS AREA DESIGNATIONS (county-level screen; not a site-boundary or permit determination):';
  if (!epa) return `${heading} not assessed in this SED. Do not state attainment or nonattainment status from this section.`;
  const lines = [`${heading} ${describeEpaDesignation(epa)}.`];
  const reading = readEpaDesignation(epa);
  if (reading !== 'nonattainment' && reading !== 'maintenance') return lines.join('\n');
  const designations = epa.designations ?? [];
  const maintenance = designations.filter(d => d.status === 'maintenance');
  if (maintenance.length > 0) {
    lines.push(`Maintenance areas (redesignated to attainment): ${maintenance
      .map(d => `${d.naaqs} (${d.area}${d.extent === 'part' ? '; part of the county' : ''}${d.redesignatedOn ? `; redesignated ${d.redesignatedOn}` : ''})`)
      .join('; ')}.`);
  }
  if (epa.classification) lines.push(`Most stringent current 8-hour ozone classification: ${epa.classification}.`);
  // EPA-7: where EPA's boundary resolved a part-county area at the site
  // point, the listing above says so; only unresolved part-county areas keep
  // the Part 81 caveat.
  const siteOzone = siteOzoneClassLine(epa);
  if (siteOzone) lines.push(siteOzone);
  const part = designations.filter(d => d.extent === 'part');
  const resolved = part.filter(d => resolvedContainment(d) !== null);
  if (resolved.length > 0) {
    lines.push('Site-point containment is from EPA\'s nonattainment-area boundaries (EPA OAR/OAQPS GIS, point-in-polygon at the site query point): a screening result; 40 CFR Part 81 is the legal boundary.');
  }
  if (part.length > resolved.length) {
    lines.push(resolved.length > 0
      ? 'Other part-county areas do not establish that the site lies inside the area; confirm against the area boundary in 40 CFR Part 81.'
      : 'Part-county areas do not establish that the site lies inside the area; confirm against the area boundary in 40 CFR Part 81.');
  }
  return lines.join('\n');
}
